import { ArrayUtils, Logger, Utils, NumberUtils, StringUtils } from "neo-common";
import { ClientHost, Communication, GameEngine, Plugins, Platforms } from "neo-games";
import { AppContext } from "../../Common/AppContext";
import { GameManager } from "./GameManager";
import { GameData } from "../Channels/Common/Datas/GameData";
import { BetManager } from "./BetManager";
import { BalanceManager } from "./BalanceManager";
import { HtmlController } from "../HTMLComponents/HtmlController";
import { ModuleManager } from "./ModuleManager";
import { GameConfiguration } from "../../Common/GameConfiguration";
import { Languages } from "../Utils/Languages";
import { SimilarGames } from "../Channels/Common/SimilarGames/SimilarGames";
import { GameApp } from "../GameApp";
import { Scaling } from "../Utils/Scaling";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";
import { UserAction } from "../HTMLComponents/UserAction";
import { QuickspinDialog } from "../Channels/Common/Dialogs/QuickspinDialog";
import { Jackpot } from "../Channels/Common/Bonuses/Jackpot";

type InitResponse = Communication.Game.InitResponse;
var GameEvents = ClientHost.GameEvents;

type ServerTimeResponse = Communication.Game.ServerTimeResponse;

export class DataManager {
    private static _gameData: { [data: string]: string } = {};

    public static DisplayCurrencies: { [data: string]: string } = null;

    private static serverTimeOffset: number = -1;

    private static time: string = "";

    private static utcTime: string = "";

    private static serverTimeResponseTimestamp: number = 0;

    private static TrustedUserActions: Array<string> = [];

    static GameRoundClosed: boolean = false;
    static Collectable: boolean = true;
    static GameLimit: boolean = false;

    static AutoplayCount: string = "∞";
    static AutoplayLossLimit: string = "∞";
    static AutoplayWinLimit: string = "∞";
    private static _autoplay: boolean = false;
    static AutoplayEnabled: boolean = true;
    static AutoplayAfterGamble: boolean = false;

    static QuickspinEnabled: boolean = false;
    private static _quickstopTimes: Array<number> = [];

    static VibrationEnabled: boolean = false;

    static HelpDataGameLimit: number = 0;
    static HelpDataGambleLimit: number = 0;

    static BackSoundsPlayingFromGame: boolean = true;

    static SimilarGames: SimilarGames;
    static AutoplayStarted: boolean = false;

    static set Autoplay(value: boolean) {
        if (DataManager._autoplay != value) {
            if (value) {
                DataManager.AutoplayStarted = true;
                Platforms.Platforms.autoplayStarted();
            } else {
                Platforms.Platforms.autoplayFinished({
                    autoplayCount: this.AutoplayCount,
                    autoplayLossLimit: this.AutoplayLossLimit,
                    autoplayWinLimit: this.AutoplayWinLimit,
                });
            }

            DataManager._autoplay = value;
        } else {
            DataManager.AutoplayStarted = false;
        }
    }

    static get Autoplay() {
        return DataManager._autoplay;
    }

    static controlAutoplay(): void {
        if (GameApp.gameClient != null) {
            GameApp.gameClient.setAutoplay(DataManager.Autoplay);
        }
    }

    static autoplayEnabled(): boolean {
        if (AppContext.gameConfiguration.AutoplayModeEnabled && DataManager.AutoplayEnabled) {
            return true;
        }
        return false;
    }

    static quickspinEnabled(): boolean {
        return DataManager.gameEnableQuickSpin() && DataManager.QuickspinEnabled;
    }

    static gameEnableQuickSpin(): boolean {
        return (
            AppContext.gameConfiguration.GameEnableReelsStop &&
            AppContext.gameConfiguration.MinGameRoundDuration == 0 &&
            AppContext.gameConfiguration.GameRoundMinSpan == 0 &&
            AppContext.gameConfiguration.GameEnableQuickSpin
        );
    }

    static quickspinSet(toValue?: boolean): boolean {
        if (DataManager.gameEnableQuickSpin()) {
            if (typeof toValue === "boolean") {
                DataManager.QuickspinEnabled = toValue;
                HtmlController.updateDataElement(UserAction.ActionQuickspinToggle, toValue ? "true" : "false");
            } else {
                DataManager.QuickspinEnabled = !DataManager.QuickspinEnabled;
                HtmlController.updateDataElement(
                    UserAction.ActionQuickspinToggle,
                    DataManager.QuickspinEnabled ? "true" : "false",
                );
            }
            if (DEBUG) {
                this.log("Quickspin " + (DataManager.QuickspinEnabled ? "on" : "off"));
            }
        }
        Platforms.Platforms.quickSpinEnabled(DataManager.QuickspinEnabled);
        return DataManager.QuickspinEnabled;
    }

    static quickspinStopped() {
        if (DataManager._quickstopTimes != null) {
            DataManager._quickstopTimes.push(Date.now() * 0.001);

            //we have enough quickstop clicks, check interval
            if (DataManager._quickstopTimes.length == AppContext.gameConfiguration.QuickSpinDialogCount) {
                let timeDiff =
                    DataManager._quickstopTimes[DataManager._quickstopTimes.length - 1] -
                    DataManager._quickstopTimes[0];

                if (timeDiff < AppContext.gameConfiguration.QuickSpinDecayDelay) {
                    if (DataManager.gameEnableQuickSpin() && !DataManager.QuickspinEnabled) {
                        QuickspinDialog.openDialog();
                        DataManager._quickstopTimes = null;
                    }
                } else {
                    DataManager._quickstopTimes.shift();
                }
            }
        }
    }

    static vibrationSet(toValue?: boolean): boolean {
        if (AppContext.gameConfiguration.GameEnableVibration) {
            if (typeof toValue === "boolean") {
                DataManager.VibrationEnabled = toValue;
                HtmlController.updateDataElement(UserAction.ActionVibrationToggle, toValue ? "true" : "false");
            } else {
                DataManager.VibrationEnabled = !DataManager.VibrationEnabled;
                HtmlController.updateDataElement(
                    UserAction.ActionVibrationToggle,
                    DataManager.VibrationEnabled ? "true" : "false",
                );
            }
            if (DEBUG) {
                this.log("Vibration " + (DataManager.VibrationEnabled ? "on" : "off"));
            }
        }

        return DataManager.VibrationEnabled;
    }

    static updateResponseData(init?: boolean): void {
        DataManager.GameRoundClosed = false;

        if (!Utils.isNullOrUndefined(GameManager.gameResponse)) {
            let status: any = GameManager.gameResponse.status;

            if (status != null && status === "closed") {
                DataManager.GameRoundClosed = true;
            }

            if (GameManager.gameResponse.limits != null) {
                DataManager.GameLimit = true;
            } else {
                DataManager.GameLimit = false;
            }

            if (init || !(FreeRoundBonus.isLastFrb && FreeRoundBonus.winPayMethod === "cumulative")) {
                // Don't set balance in last cumulative FRB (except init state)
                BalanceManager.setBalance();
            }

            if (init) {
                var initResponse = GameManager.gameResponse as InitResponse;
                if (initResponse.initData != null && initResponse.initData.currency != null) {
                    DataManager.updateGameDataFromModule(GameData.DataCurrency, initResponse.initData.currency);
                }

                BetManager.initializeBet();
                DataManager.configureHelpData();

                if (
                    GameManager.gameResponse.balance != null &&
                    GameManager.gameResponse.balance.bonus != null &&
                    GameManager.gameResponse.balance.bonus >= BetManager.Bet
                ) {
                    ModuleManager.changeBetColor(true);
                }
                DataManager.updateDataElements();
            }

            if (GameManager.gameResponse.roundId != null) {
                const elements = document.getElementsByClassName("roundId");

                for (let i = 0; i < elements.length; i++) {
                    const element = <HTMLElement>elements[i];

                    if (element != null) {
                        if (!AppContext.gameConfiguration.GameRoundIdVisible) {
                            element.style.opacity = "0";
                        } else if (GameManager.gameResponse.roundId === -1) {
                            element.style.opacity = "1";
                        }
                    }
                }

                DataManager.updateGameDataFromModule(
                    GameData.DataRoundId,
                    GameManager.gameResponse.roundId === -1 ? "DEMO" : GameManager.gameResponse.roundId.toString(),
                );
            }

            DataManager.updateTotalWin();
        }

        if (!Jackpot.hasMiniGame()) {
            BalanceManager.poolingBalance();
        }

        if (
            GameManager.gameResponse["progressiveBonus"] &&
            GameManager.gameResponse["progressiveBonus"].offline === true
        ) {
            Jackpot.showError();
        } else {
            Jackpot.reinitialize();
        }
    }

    /**
     *
     */
    static updateTotalWin(): void {
        if (FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative") {
            // FRB cumulative win pay method
            if (
                GameManager.gameResponse["freeRounds"] != null &&
                GameManager.gameResponse["freeRounds"]["totalWin"] != null
            ) {
                if (DataManager.GameRoundClosed) {
                    // Standard case
                    const totalWin = GameManager.gameResponse["freeRounds"]["totalWin"];
                    DataManager.updateGameDataFromModule(GameData.DataTotalWin, totalWin.toString());

                    if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                        DataManager.updateGameDataFromModule(
                            GameData.DataTotalWinDenom,
                            parseInt(NumberUtils.devide(totalWin, BetManager.CoinSize).toString()).toString(),
                        );
                    }
                } else {
                    if (GameManager.gameResponse.totalWin != null) {
                        const totalWin = NumberUtils.add(
                            GameManager.gameResponse["freeRounds"]["totalWin"],
                            GameManager.gameResponse.totalWin,
                        );
                        DataManager.updateGameDataFromModule(GameData.DataTotalWin, totalWin.toString());

                        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                            DataManager.updateGameDataFromModule(
                                GameData.DataTotalWinDenom,
                                parseInt(NumberUtils.devide(totalWin, BetManager.CoinSize).toString()).toString(),
                            );
                        }
                    }
                }
            }
        } else if (GameManager.gameResponse.totalWin != null) {
            // Standard case
            DataManager.updateGameDataFromModule(GameData.DataTotalWin, GameManager.gameResponse.totalWin.toString());
            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                DataManager.updateGameDataFromModule(
                    GameData.DataTotalWinDenom,
                    parseInt(
                        NumberUtils.devide(GameManager.gameResponse.totalWin, BetManager.CoinSize).toString(),
                    ).toString(),
                );
            }
        }
    }

    static updateTotalGain(calculate: boolean = false, customWin: number = 0): void {
        if (
            GameManager.gameResponse.totalGain != null &&
            AppContext.gameConfiguration.StatusBar.indexOf(Plugins.StatusBar.Types.TOTAL_GAIN) > -1
        ) {
            let totalGain = GameManager.gameResponse.totalGain;
            if (calculate) {
                const bet = FreeRoundBonus.inFrb ? 0 : BetManager.Bet;
                totalGain = parseFloat(NumberUtils.subtract(totalGain, bet));

                if (GameManager.gameResponse.totalWin > 0 && !DataManager.GameRoundClosed) {
                    totalGain = parseFloat(NumberUtils.add(totalGain, GameManager.gameResponse.totalWin));
                }
            }

            if (customWin > 0) {
                totalGain = parseFloat(NumberUtils.add(totalGain, customWin));
            }

            GameManager.gameResponse.totalGain = totalGain;
            const statusBar = Plugins.StatusBar.getInstance();
            const resize = !statusBar.hasActiveWidget();
            statusBar.add(
                Plugins.StatusBar.Types.TOTAL_GAIN,
                "totalGain",
                Languages.getRid(
                    "SB_INFO_TOTAL_GAIN",
                    Languages.formatText(Languages.FormatMoney, totalGain.toString()),
                ) +
                    " " +
                    DataManager.getDataById(GameData.DataCurrency),
            );

            if (resize) {
                Scaling.resizeRoot();
            }
        }
    }

    static updateGameDataFromModule(dataId: string, value: string): void {
        if (dataId === GameData.DataCurrency) {
            if (Utils.isNullOrUndefined(DataManager.DisplayCurrencies)) {
                DataManager.DisplayCurrencies = {};

                AppContext.gameConfiguration.DisplayCurrencies.forEach((rawPair) => {
                    const parsedPair = ArrayUtils.stringArray(rawPair);

                    DataManager.DisplayCurrencies[parsedPair[0]] = parsedPair[1];
                });
            }

            if (DataManager.DisplayCurrencies.hasOwnProperty(value)) {
                DataManager._gameData[dataId] = DataManager.DisplayCurrencies[value];
                return;
            }
        }

        DataManager._gameData[dataId] = value;
    }

    static getDataById(dataId: string): string {
        if (DataManager._gameData[dataId] != null) {
            return DataManager._gameData[dataId];
        }
        return null;
    }

    static updateDataElement(dataId: string, customValue?: string): void {
        if (customValue != null) {
            HtmlController.updateDataElement(dataId, customValue);
        } else {
            if (DataManager._gameData[dataId] != null) {
                HtmlController.updateDataElement(dataId, DataManager._gameData[dataId]);
            }
        }
    }

    static updateDataElements(): void {
        HtmlController.updateDataElements(DataManager._gameData);
        DataManager.updateTotalGain();
    }

    static getServerTime(): void {
        if (GameManager.gameClient) {
            GameManager.gameClient.sendServerTimerRequest().then((response: ServerTimeResponse) => {
                DataManager.serverTimeReceived(response);

                if (
                    AppContext.gameConfiguration.UseServerTime &&
                    StringUtils.isNullOrEmpty(AppContext.gameConfiguration.ApplicationGatewayUrl)
                ) {
                    setTimeout(() => {
                        DataManager.getServerTime();
                    }, 30000);
                }
            });
        }
    }

    static serverTimeReceived(response: ServerTimeResponse): void {
        if (response == null) {
            return;
        }

        if (response.utcTime == null && response.time == null) {
            return;
        }

        DataManager.utcTime = response.utcTime;
        DataManager.time = response.time;
        DataManager.serverTimeResponseTimestamp = Date.now();

        if (DataManager.utcTime != null) {
            if (DataManager.utcTime.indexOf("Z") == -1) {
                DataManager.utcTime = DataManager.utcTime + "Z";
            }

            Platforms.Platforms.clockSync(Date.now() - Date.parse(DataManager.utcTime));
        }

        if (DataManager.serverTimeOffset === -1) {
            let currentTime = new Date().getTime();
            let serverTime = Date.parse(DataManager.utcTime ? DataManager.utcTime.replace("Z", "") : DataManager.time);

            DataManager.serverTimeOffset = serverTime - currentTime;
        }
    }

    static getFormattedTime(time: Date): string {
        if (time == null) {
            return null;
        }

        //{"time":"2023-09-21T12:00:02.3442838","utcTime":"2023-09-21T10:00:02.3442838"}
        const year = time.getFullYear();
        const month = DataManager.zerosPad(time.getMonth() + 1, 2);
        const day = DataManager.zerosPad(time.getDate(), 2);
        const hours = DataManager.zerosPad(time.getHours(), 2);
        const minutes = DataManager.zerosPad(time.getMinutes(), 2);
        const seconds = DataManager.zerosPad(time.getSeconds(), 2);
        const milliseconds = time.getMilliseconds();

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
    }

    static zerosPad(num: number, size: number): string {
        let s = num + "";
        while (s.length < size) s = "0" + s;
        return s;
    }

    static getTimeData(): { utcTime: string; machineLocalTimeZone: number } {
        const currentTime = new Date();
        const utcTime = DataManager.getUTCTime();
        const machineLocalTimeZone = currentTime.getTimezoneOffset() * -1; //in minutes

        return {
            utcTime: utcTime,
            machineLocalTimeZone: machineLocalTimeZone,
        };
    }

    static getUTCTime(): string {
        if (!StringUtils.isNullOrEmpty(DataManager.utcTime)) {
            const currentTime = new Date();
            const utcResponseTimestamp = Date.parse(DataManager.utcTime);
            const lastResponseDiff = currentTime.getTime() - DataManager.serverTimeResponseTimestamp;

            return new Date(utcResponseTimestamp + lastResponseDiff).toISOString();
        }

        return new Date().toISOString();
    }

    static getGameTime(utcTime: string): string {
        if (utcTime.indexOf("Z") == -1) {
            utcTime = utcTime + "Z";
        }

        const utcTimestamp = Date.parse(utcTime.replace("Z", ""));

        if (AppContext.gameConfiguration.UseServerTime) {
            const offset = DataManager.utcTime ? DataManager.serverTimeOffset : 0;
            return DataManager.getFormattedTime(new Date(utcTimestamp - offset));
        }

        return DataManager.getFormattedTime(new Date(utcTime));
    }

    static getNonUTCGameTime(): string {
        if (!AppContext.gameConfiguration.UseServerTime) {
            return DataManager.getFormattedTime(new Date());
        }

        if (!StringUtils.isNullOrEmpty(DataManager.time)) {
            const currentTime = new Date();
            const utcResponseTimestamp = Date.parse(DataManager.time);
            const lastResponseDiff = currentTime.getTime() - DataManager.serverTimeResponseTimestamp;
            return DataManager.getFormattedTime(
                new Date(utcResponseTimestamp + lastResponseDiff - DataManager.serverTimeOffset),
            );
        }

        return DataManager.getFormattedTime(new Date());
    }

    static showTime(): void {
        if (!AppContext.gameConfiguration.UIShowTime) {
            return;
        }

        let today = new Date(DataManager.getNonUTCGameTime());

        if (DataManager.utcTime != null) {
            const timeData = DataManager.getTimeData();
            today = new Date(DataManager.getGameTime(timeData.utcTime));
        }

        let h = today.getHours();
        let hFixed: string = h + "";
        if (h < 10) {
            hFixed = "0" + h;
        }

        let m = today.getMinutes();
        let mFixed: string = m + "";
        if (m < 10) {
            mFixed = "0" + m;
        }
        DataManager.updateDataElement("TIME", hFixed + ":" + mFixed);
        setTimeout(() => {
            DataManager.showTime();
        }, 1000);
    }

    static showSessionTime(time: number): void {
        if (AppContext.gameConfiguration.GUIShowSessionTime) {
            if (time < 0) {
                time = 0;
            }

            let sec_num: number = time;
            let hours: number = Math.floor(sec_num / 3600);
            let minutes: number = Math.floor((sec_num - hours * 3600) / 60);
            let seconds: number = sec_num - hours * 3600 - minutes * 60;

            let hoursS: string = hours.toString();
            let minutesS: string = minutes.toString();
            let secondsS: string = seconds.toString();

            if (hours < 10) {
                hoursS = "0" + hours;
            }
            if (minutes < 10) {
                minutesS = "0" + minutes;
            }
            if (seconds < 10) {
                secondsS = "0" + seconds;
            }

            let pre: string = AppContext.gameConfiguration.UIShowTime ? "&nbsp;|&nbsp;" : "";

            DataManager.updateDataElement("SESSION_TIME", pre + hoursS + ":" + minutesS + ":" + secondsS);
            time++;
            setTimeout(() => {
                DataManager.showSessionTime(time);
            }, 1000);
        }
    }

    static configureGameData(): void {
        if (AppContext.gameConfiguration.StatusBar.indexOf(Plugins.StatusBar.Types.StatusBarMessage) > -1) {
            if (
                AppContext.gameConfiguration.StatusBarData != null &&
                AppContext.gameConfiguration.StatusBarData[Plugins.StatusBar.Types.StatusBarMessage] != null
            ) {
                const statusBar = Plugins.StatusBar.getInstance();
                statusBar.add(
                    Plugins.StatusBar.Types.StatusBarMessage,
                    "statusBarMessage",
                    AppContext.gameConfiguration.StatusBarData[Plugins.StatusBar.Types.StatusBarMessage],
                );
                Scaling.resizeRoot();
            }
        }

        if (!DataManager.gameEnableQuickSpin()) {
            DataManager.hideDataElements("quickSpinSettings");
        }

        if (!AppContext.gameConfiguration.GameEnableVibration) {
            DataManager.hideDataElements("vibrationSettings");
        }

        if (!DataManager.hasSettingsMenu()) {
            DataManager.hideDataElements("helpGameSettings");
        }

        const gameTitle = document.getElementById("gameTitle");
        if (gameTitle != null) {
            DataManager.updateDataElement(GameData.DataGameTitle, gameTitle.innerHTML);

            const gameHeadline = document.getElementById("gameHeadline");
            if (gameHeadline != null) {
                gameHeadline.style.display = "none";
            }
        }

        if (!AppContext.gameConfiguration.GameEnableReelsStop) {
            DataManager.hideDataElements("GameEnableReelsStop");
        } else {
            DataManager.hideDataElements("GameDisableReelsStop");
        }
    }

    private static hasSettingsMenu(): boolean {
        if (DataManager.gameEnableQuickSpin()) {
            return true;
        }

        return false;
    }

    static configureHelpData(): void {
        DataManager.showDataElements("gambleAttempts");
        DataManager.showDataElements("helpGameLimit");
        DataManager.showDataElements("helpGambleLimit");
        DataManager.showDataElements("helpGambleCountLimit");
        DataManager.showDataElements("helpJoker");
        DataManager.showDataElements("helpRTP");
        DataManager.showDataElements("totalRTP");
        DataManager.hideDataElements("helpVersion");
        DataManager.showDataElements("helpJackpotVisibleLimit");
        DataManager.showDataElements("helpJackpotContributionAll");
        DataManager.showDataElements("helpJackpotQualifyingBetsAll");
        DataManager.showDataElements("helpJackpotQualifyingBetsMin");
        DataManager.showDataElements("helpJackpotQualifyingBetsMax");
        DataManager.hideDataElements("helpShowCumulativeWinLimit");
        DataManager.hideDataElements("helpMPP");
        DataManager.hideDataElements("helpMPF");

        if (AppContext != null) {
            if (GameManager.gameResponse != null) {
                if (GameManager.gameResponse["helpData"] == null) {
                    DataManager.hideDataElements("gambleAttempts");
                    DataManager.hideDataElements("helpGameLimit");
                    DataManager.hideDataElements("helpGambleLimit");
                    DataManager.hideDataElements("helpGambleCountLimit");
                    DataManager.hideDataElements("helpJoker");
                    DataManager.hideDataElements("helpRTP");
                    DataManager.hideDataElements("totalRTP");
                    DataManager.hideDataElements("helpMPP");
                    DataManager.hideDataElements("helpMPF");
                } else {
                    if (GameManager.gambleResponse == null) {
                        DataManager.hideDataElements("hlpGambleSection");
                        DataManager.showDataElements("hlpNoGambleSection", "");
                    } else {
                        DataManager.hideDataElements("hlpNoGambleSection");
                        DataManager.showDataElements("hlpGambleSection", "");
                    }

                    if (GameManager.gameResponse["helpData"]["gambleCountLimit"] == null) {
                        DataManager.hideDataElements("gambleAttempts");
                    } else {
                        if (
                            GameManager.gambleResponse != null &&
                            GameManager.gambleResponse["totalCount"] != null &&
                            GameManager.gambleResponse["currentCount"] != null
                        ) {
                            DataManager.updateDataElement(
                                GameData.DataGambleAttempts,
                                (
                                    GameManager.gambleResponse["totalCount"] -
                                    GameManager.gambleResponse["currentCount"]
                                ).toString(),
                            );
                        }
                    }

                    if (GameManager.gameResponse["helpData"]["gameLimit"] != null) {
                        DataManager.HelpDataGameLimit = GameManager.gameResponse["helpData"]["gameLimit"];
                        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                            DataManager.updateGameDataFromModule(
                                GameData.DataHelpGameLimit,
                                parseInt(
                                    NumberUtils.devide(DataManager.HelpDataGameLimit, BetManager.CoinSize).toString(),
                                ).toString(),
                            );
                            DataManager.updateDataElement(
                                GameData.DataHelpGameLimit,
                                parseInt(
                                    NumberUtils.devide(DataManager.HelpDataGameLimit, BetManager.CoinSize).toString(),
                                ).toString(),
                            );
                        } else {
                            DataManager.updateGameDataFromModule(
                                GameData.DataHelpGameLimit,
                                Languages.formatText(
                                    Languages.FormatMoney,
                                    GameManager.gameResponse["helpData"]["gameLimit"],
                                ) +
                                    " " +
                                    DataManager.getDataById(GameData.DataCurrency),
                            );
                            DataManager.updateDataElement(
                                GameData.DataHelpGameLimit,
                                Languages.formatText(
                                    Languages.FormatMoney,
                                    GameManager.gameResponse["helpData"]["gameLimit"],
                                ) +
                                    " " +
                                    DataManager.getDataById(GameData.DataCurrency),
                            );
                        }
                    }

                    if (
                        GameManager.gameResponse["helpData"]["gameLimit"] == null ||
                        !AppContext.gameConfiguration.HelpShowGameLimit
                    ) {
                        DataManager.hideDataElements("helpGameLimit");
                    }

                    if (GameManager.gameResponse["helpData"]["gambleLimit"] != null) {
                        DataManager.HelpDataGambleLimit = GameManager.gameResponse["helpData"]["gambleLimit"];
                        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                            DataManager.updateGameDataFromModule(
                                GameData.DataHelpGambleLimit,
                                parseInt(
                                    NumberUtils.devide(DataManager.HelpDataGambleLimit, BetManager.CoinSize).toString(),
                                ).toString(),
                            );
                            DataManager.updateDataElement(
                                GameData.DataHelpGambleLimit,
                                parseInt(
                                    NumberUtils.devide(DataManager.HelpDataGambleLimit, BetManager.CoinSize).toString(),
                                ).toString(),
                            );
                        } else {
                            DataManager.updateGameDataFromModule(
                                GameData.DataHelpGambleLimit,
                                Languages.formatText(
                                    Languages.FormatMoney,
                                    GameManager.gameResponse["helpData"]["gambleLimit"],
                                ) +
                                    " " +
                                    DataManager.getDataById(GameData.DataCurrency),
                            );
                            DataManager.updateDataElement(
                                GameData.DataHelpGambleLimit,
                                Languages.formatText(
                                    Languages.FormatMoney,
                                    GameManager.gameResponse["helpData"]["gambleLimit"],
                                ) +
                                    " " +
                                    DataManager.getDataById(GameData.DataCurrency),
                            );
                        }
                    } else {
                        DataManager.hideDataElements("helpGambleLimit");
                    }

                    if (GameManager.gameResponse["helpData"]["gambleCountLimit"] == null) {
                        DataManager.hideDataElements("helpGambleCountLimit");
                    } else {
                        DataManager.updateDataElement(
                            GameData.DataHelpGambleCountLimit,
                            GameManager.gameResponse["helpData"]["gambleCountLimit"] + " ",
                        );
                    }

                    if (
                        GameManager.gameResponse["helpData"]["gambleWithJoker"] == null ||
                        !GameManager.gameResponse["helpData"]["gambleWithJoker"]
                    ) {
                        DataManager.hideDataElements("helpJoker");
                    }

                    let rtp = GameManager.gameResponse["helpData"]["rtp"];
                    let totalRtp = GameManager.gameResponse["helpData"]["totalRtp"];
                    let progressiveBonusRtp = GameManager.gameResponse["helpData"]["progressiveBonusRtp"];
                    let ridTotalRTP = AppContext.gameConfiguration.UseJackpotLocalization
                        ? "HLP_AD_INFO_TOTAL_RTP_J"
                        : "HLP_AD_INFO_TOTAL_RTP";
                    let statusBarRtp: string;

                    if (
                        totalRtp != null &&
                        progressiveBonusRtp != null &&
                        rtp != null &&
                        AppContext.gameConfiguration.HelpShowRTP
                    ) {
                        // BaseGameAndProgressiveBonus
                        DataManager.hideDataElements("helpRTP");
                        DataManager.setDataElements(
                            "totalRTP",
                            Languages.getRid(
                                ridTotalRTP,
                                totalRtp.toString(),
                                rtp.toString(),
                                progressiveBonusRtp.toString(),
                            ),
                        );
                        statusBarRtp = totalRtp.toString();
                    } else if (rtp != null && AppContext.gameConfiguration.HelpShowRTP) {
                        // BaseGame
                        DataManager.hideDataElements("totalRTP");
                        DataManager.updateDataElement(GameData.DataHelpRTP, rtp.toString());
                        statusBarRtp = rtp.toString();
                    } else {
                        // Disabled
                        DataManager.hideDataElements("helpRTP");
                        DataManager.hideDataElements("totalRTP");
                    }

                    if (
                        statusBarRtp != null &&
                        AppContext.gameConfiguration.StatusBar.indexOf(Plugins.StatusBar.Types.RTP) > -1
                    ) {
                        const statusBar = Plugins.StatusBar.getInstance();
                        statusBar.add(
                            Plugins.StatusBar.Types.RTP,
                            "rtp",
                            Languages.getRid("SB_INFO_RTP", statusBarRtp.toString()),
                        );
                        Scaling.resizeRoot();
                    }

                    if (
                        GameManager.gameResponse["helpData"]["version"] == null ||
                        !AppContext.gameConfiguration.HelpShowGameModuleVersion
                    ) {
                        DataManager.hideDataElements("helpVersion");
                    } else {
                        DataManager.updateDataElement(
                            GameData.DataHelpVersion,
                            GameManager.gameResponse["helpData"]["version"].toString(),
                        );
                        DataManager.showDataElements("helpVersion");
                    }

                    if (GameManager.gameResponse["helpData"]["maxPayoutProbability"] != null) {
                        if (
                            AppContext.gameConfiguration.StatusBar.indexOf(
                                Plugins.StatusBar.Types.MaxPayoutProbability,
                            ) > -1
                        ) {
                            const statusBar = Plugins.StatusBar.getInstance();
                            statusBar.add(
                                Plugins.StatusBar.Types.MaxPayoutProbability,
                                "maxPayoutProbability",
                                Languages.getRid(
                                    "SB_INFO_MAX_PAYOUT_PROBABILITY",
                                    GameManager.gameResponse["helpData"]["maxPayoutProbability"],
                                ),
                            );
                            Scaling.resizeRoot();
                        }

                        if (
                            AppContext.gameConfiguration.HelpShowMaxPayoutProbabilityFrequency &&
                            GameManager.gameResponse["helpData"]["maxTotalPayoutProbability"] != null
                        ) {
                            DataManager.setDataElements(
                                "helpMPF",
                                Languages.getRid(
                                    "HLP_AD_INFO_MAX_PAYOUT_FREQUENCY",
                                    GameManager.gameResponse["helpData"]["maxPayoutProbability"].toString(),
                                    GameManager.gameResponse["helpData"]["maxTotalPayoutProbability"].toString(),
                                ),
                            );
                            DataManager.showDataElements("helpMPF");
                        } else if (AppContext.gameConfiguration.HelpShowMaxPayoutProbability) {
                            DataManager.updateDataElement(
                                GameData.DataHelpMaxPayoutProbability,
                                GameManager.gameResponse["helpData"]["maxPayoutProbability"].toString(),
                            );
                            DataManager.showDataElements("helpMPP");
                        }
                    }

                    if (
                        AppContext.gameConfiguration.ProgressiveBonusTicketConstraint == "WithinGameRound" &&
                        AppContext.gameConfiguration.ProgressiveBonusHelpShowCumulativeWinLimit &&
                        GameManager.gameResponse["helpData"]["gameLimit"] != null
                    ) {
                        DataManager.showDataElements("helpShowCumulativeWinLimit");
                    }
                }
            }

            //process information stored in initData
            if (GameManager.baseResponse.initData != null) {
                if (
                    GameManager.baseResponse.initData["customData"] != null &&
                    GameManager.baseResponse.initData["customData"]["maxBuyFeatureBetLimit"] != null &&
                    GameManager.baseResponse.initData["customData"]["maxBuyFeatureBetLimit"] > 0
                ) {
                    HtmlController.updateDataElement(
                        GameData.DataMaxBuyFeatureBetLimit,
                        Languages.formatText(
                            Languages.FormatMoney,
                            GameManager.baseResponse.initData["customData"]["maxBuyFeatureBetLimit"],
                        ) +
                            "\u00A0" +
                            DataManager.getDataById(GameData.DataCurrency),
                    );
                } else {
                    DataManager.hideDataElements("helpMaxBuyFeatureBetLimit");
                }
            }

            if (BetManager.TotalBets.length > 0) {
                DataManager.updateDataElement(
                    GameData.DataHelpMinBet,
                    Languages.formatText(Languages.FormatMoney, Math.min(...BetManager.TotalBets).toString()) +
                        " " +
                        DataManager.getDataById(GameData.DataCurrency),
                );
                DataManager.updateDataElement(
                    GameData.DataHelpMaxBet,
                    Languages.formatText(Languages.FormatMoney, Math.max(...BetManager.TotalBets).toString()) +
                        " " +
                        DataManager.getDataById(GameData.DataCurrency),
                );
            } else {
                DataManager.hideDataElements("helpMinBet");
                DataManager.hideDataElements("helpMaxBet");
            }

            if (AppContext.gameConfiguration.ProgressiveBonusContributeOnlyQualified) {
                DataManager.hideDataElements("helpJackpotContributionAll");
                DataManager.showDataElements("helpJackpotContributionQualifying");
            } else {
                DataManager.showDataElements("helpJackpotContributionAll");
                DataManager.hideDataElements("helpJackpotContributionQualifying");
            }

            if (
                AppContext.gameConfiguration.ProgressiveBonusVisibleMax > 0 &&
                AppContext.gameConfiguration.ProgressiveBonusHelpShowMaxVisibleLimit
            ) {
                DataManager.updateDataElement(
                    GameData.DataHelpJackpotVisibleLimit,
                    Languages.formatText(
                        Languages.FormatMoney,
                        AppContext.gameConfiguration.ProgressiveBonusVisibleMax.toString(),
                    ) +
                        " " +
                        DataManager.getDataById(GameData.DataCurrency),
                );
            } else {
                DataManager.hideDataElements("helpJackpotVisibleLimit");
            }

            if (AppContext.gameConfiguration.ProgressiveBonusMinQualifyingBet > 0) {
                DataManager.hideDataElements("helpJackpotQualifyingBetsAll");
                DataManager.updateDataElement(
                    GameData.DataHelpJackpotQualifyingBetsMin,
                    Languages.formatText(
                        Languages.FormatMoney,
                        AppContext.gameConfiguration.ProgressiveBonusMinQualifyingBet.toString(),
                    ) +
                        " " +
                        DataManager.getDataById(GameData.DataCurrency),
                );
            } else {
                DataManager.hideDataElements("helpJackpotQualifyingBetsMin");
            }

            if (AppContext.gameConfiguration.ProgressiveBonusMaxQualifyingBet > 0) {
                DataManager.hideDataElements("helpJackpotQualifyingBetsAll");
                DataManager.updateDataElement(
                    GameData.DataHelpJackpotQualifyingBetsMax,
                    Languages.formatText(
                        Languages.FormatMoney,
                        AppContext.gameConfiguration.ProgressiveBonusMaxQualifyingBet.toString(),
                    ) +
                        " " +
                        DataManager.getDataById(GameData.DataCurrency),
                );
            } else {
                DataManager.hideDataElements("helpJackpotQualifyingBetsMax");
            }

            if (AppContext.gameConfiguration.AutoplayMode === "simple") {
                DataManager.hideDataElements("helpAutoplayBasic");
                DataManager.hideDataElements("helpAutoplayAdvanced");
            } else if (AppContext.gameConfiguration.AutoplayMode === "advanced") {
                DataManager.hideDataElements("helpAutoplayBasic");
                DataManager.hideDataElements("helpAutoplaySimple");
            } else {
                DataManager.hideDataElements("helpAutoplaySimple");
                DataManager.hideDataElements("helpAutoplayAdvanced");
            }

            if (!AppContext.gameConfiguration.AutoplayModeEnabled) {
                DataManager.hideDataElements("helpAutoplayEnabled");
            } else {
                DataManager.hideDataElements("helpAutoplayDisabled");
            }

            if (!AppContext.gameConfiguration.ShowHomeButton) {
                DataManager.hideDataElements("gameHomeBtn");
            }

            if (!AppContext.gameConfiguration.ShowGameRoundsHistory) {
                DataManager.hideDataElements("historyBtn");
                DataManager.hideDataElements("helpHistoryBtn");
            }

            if (AppContext.gameConfiguration.ExpirationResolveHours > 0) {
                DataManager.updateDataElement(
                    GameData.DataExpirationResolveHours,
                    AppContext.gameConfiguration.ExpirationResolveHours.toString(),
                );
            } else {
                DataManager.hideDataElements("expirationResolveHours");
            }
        }
    }

    static setDataElements(cls: string, text: string): void {
        var elements: HTMLCollectionOf<Element> = document.getElementsByClassName(cls);
        for (var i: number = 0; i < elements.length; i++) {
            var element: HTMLElement = <HTMLElement>elements[i];
            if (element != null) {
                element.innerHTML = text;
            }
        }
    }

    static hideDataElements(cls: string): void {
        var elements: HTMLCollectionOf<Element> = document.getElementsByClassName(cls);
        for (var i: number = 0; i < elements.length; i++) {
            var element: HTMLElement = <HTMLElement>elements[i];
            if (element != null) {
                element.style.display = "none";
            }
        }
    }

    static showDataElements(cls: string, display: string = "block"): void {
        var elements: HTMLCollectionOf<Element> = document.getElementsByClassName(cls);
        for (var i: number = 0; i < elements.length; i++) {
            var element: HTMLElement = <HTMLElement>elements[i];
            if (element != null) {
                element.style.display = display;
            }
        }
    }

    static cleanUpElements(): void {
        var elements: HTMLCollectionOf<Element>;
        if (AppContext != null && AppContext.gameConfiguration != null && AppContext.gameConfiguration.UseCoins) {
            elements = document.getElementsByClassName("useCoinsFalse");
        } else {
            elements = document.getElementsByClassName("useCoinsTrue");
        }

        if (elements != null && elements.length > 0) {
            while (elements.length > 0) {
                elements[0].parentNode.removeChild(elements[0]);
            }
        }

        if (AppContext.gameConfiguration.UseJackpotLocalization) {
            elements = document.getElementsByClassName("helpJackpotB");
        } else {
            elements = document.getElementsByClassName("helpJackpotJ");
        }

        if (elements != null && elements.length > 0) {
            while (elements.length > 0) {
                elements[0].parentNode.removeChild(elements[0]);
            }
        }

        elements = document.getElementsByClassName("settingTabItem");
        let hideSettingsButton = true;
        for (let i = 0; i < elements.length; i++) {
            if ((<HTMLElement>elements[i]).style.display != "none") {
                hideSettingsButton = false;
            }
        }

        if (hideSettingsButton) {
            elements = document.getElementsByClassName("settingsBtn");
            if (elements != null && elements.length > 0) {
                while (elements.length > 0) {
                    elements[0].parentNode.removeChild(elements[0]);
                }
            }
        }
    }

    static addTrustedUserAction(actionId: string): void {
        if (DataManager.TrustedUserActions.indexOf(actionId) == -1) {
            const groupActionId = DataManager.getGroupActionId(actionId);

            DataManager.TrustedUserActions.push(groupActionId);
        }
    }

    static getTrustedUserAction(groupActionId: string): boolean {
        const index = DataManager.TrustedUserActions.indexOf(groupActionId);
        if (index == -1) {
            return false;
        } else {
            DataManager.TrustedUserActions.splice(index, 1);
            return true;
        }
    }

    private static getGroupActionId(actionId: string): string {
        let result = actionId;
        switch (actionId) {
            case "BET_PLUS":
            case "BET_MINUS":
            case "BET_CYCLE":
            case "MAX_BET":
            case "COIN_SIZE_PLUS":
            case "COIN_SIZE_PLUS_CYCLE":
            case "COIN_SIZE_MINUS":
            case "PAYLINE_COUNT_PLUS":
            case "PAYLINE_COUNT_PLUS_CYCLE":
            case "PAYLINE_COUNT_MINUS":
                result = "BET_CHANGE";
                break;
            case "SPIN":
                result = "SPIN";
                break;
            case "SPIN_OR_STOP":
                if (HtmlController.ActionElements["SPIN_STOP"] != null) {
                    const btn = HtmlController.ActionElements["SPIN_STOP"][0];
                    if (btn != null && btn.instance.classList.contains("_disabled")) {
                        result = "SPIN";
                    }
                }
        }

        return result;
    }

    static stopAutoplayIfLossLimit(): void {
        if (DataManager.AutoplayLossLimit === "∞") {
            return;
        }

        let lossLimit: number = parseFloat(DataManager.AutoplayLossLimit);
        if (GameManager.baseResponse != null && GameManager.baseResponse.totalWin > 0) {
            lossLimit += GameManager.baseResponse.totalWin;
        }

        if (lossLimit - BetManager.Bet < 0) {
            DataManager.Autoplay = false;
        }
    }

    static updateLossLimit(win: number = 0): void {
        if (DataManager.AutoplayLossLimit === "∞") {
            return;
        }

        let lossLimit: number = parseFloat(DataManager.AutoplayLossLimit);

        if (win > 0) {
            lossLimit += win;
        } else {
            if (BetManager.Bet != null) {
                lossLimit = NumberUtils.toNumber(NumberUtils.subtract(lossLimit, BetManager.Bet));
            }
        }

        DataManager.AutoplayLossLimit = lossLimit + "";
    }

    static stopAutoplayIfWinLimit(): boolean {
        if (!DataManager.Autoplay) {
            return false;
        }

        if (DataManager.AutoplayWinLimit == "∞") {
            return false;
        } else {
            if (
                GameManager.gameResponse != null &&
                GameManager.gameResponse.totalWin > 0 &&
                GameManager.gameResponse.totalWin >= parseFloat(DataManager.AutoplayWinLimit)
            ) {
                DataManager.Autoplay = false;
                return true;
            }
        }
    }

    static gameState(state: string): void {
        if (AppContext.clientHost) {
            const messageId = GameManager.gameResponse.messageId;
            const unpaidWin = GameManager.gameResponse.totalWin;
            const gameRoundId = GameManager.gameResponse.roundId;
            let gameEvent: any;
            switch (state) {
                case "PrimaryGameEscrow":
                    gameEvent = GameEvents.PrimaryGameEscrow(messageId, unpaidWin, gameRoundId);
                    break;
                case "PrimaryGameStarted":
                    gameEvent = GameEvents.PrimaryGameStarted(messageId, unpaidWin, gameRoundId);
                    break;
                case "PrimaryGameEnded":
                    gameEvent = GameEvents.PrimaryGameEnded(messageId, unpaidWin, gameRoundId);
                    break;
                case "SecondaryGameEntered":
                    gameEvent = GameEvents.SecondaryGameEntered(messageId, unpaidWin, gameRoundId);
                    break;
                case "SecondaryGameEscrow":
                    gameEvent = GameEvents.SecondaryGameEscrow(messageId, unpaidWin, gameRoundId);
                    break;
                case "SecondaryGameStarted":
                    gameEvent = GameEvents.SecondaryGameStarted(messageId, unpaidWin, gameRoundId);
                    break;
                case "GameCollectEscrow":
                    gameEvent = GameEvents.GameCollectEscrow(messageId, unpaidWin, gameRoundId);
                    break;
                case "SecondaryGameCollectEscrow":
                    gameEvent = GameEvents.SecondaryGameCollectEscrow(messageId, unpaidWin, gameRoundId);
                    break;
                case "SecondaryGameEnded":
                    gameEvent = GameEvents.SecondaryGameEnded(messageId, unpaidWin, gameRoundId);
                    break;
                case "GameEnded":
                    gameEvent = GameEvents.GameEnded(messageId, unpaidWin, gameRoundId);
                    break;
                case "SecondaryGameHalved":
                    gameEvent = GameEvents.SecondaryGameHalved(messageId, unpaidWin, gameRoundId);
                    break;
                case "AutoPlayStarted":
                    gameEvent = GameEvents.AutoPlayStarted(messageId, unpaidWin, gameRoundId);
                    break;
                case "AutoPlayEnded":
                    gameEvent = GameEvents.AutoPlayEnded(messageId, unpaidWin, gameRoundId);
                    break;
                case "GamePlayDisabled":
                    gameEvent = GameEvents.GamePlayDisabled(messageId, unpaidWin, gameRoundId);
                    break;
                case "GamePlayEnabled":
                    gameEvent = GameEvents.GamePlayEnabled(messageId, unpaidWin, gameRoundId);
                    break;
                case "GameLoaded":
                    gameEvent = GameEvents.GameLoaded(messageId, unpaidWin, gameRoundId);
                    break;
                case "BonusGameEnded":
                    gameEvent = GameEvents.BonusGameEnded(messageId, unpaidWin, gameRoundId);
                    break;
                case "BonusGameEscrow":
                    gameEvent = GameEvents.BonusGameEscrow(messageId, unpaidWin, gameRoundId);
                    break;
                case "BonusGameStarted":
                    gameEvent = GameEvents.BonusGameStarted(messageId, unpaidWin, gameRoundId);
                    break;
                case "BonusGameTriggered":
                    gameEvent = GameEvents.BonusGameTriggered(messageId, unpaidWin, gameRoundId);
                    break;
            }

            if (gameEvent != null) {
                AppContext.clientHost.notifyEventFromGame(gameEvent);
            }
        }
    }

    private static log(s: string): void {
        if (DEBUG) {
            Logger.debugGroup("DataManager", s);
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Managers/DataManager.ts
 **/